import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Cloud, 
  DollarSign, 
  Unlock, 
  Shield, 
  Cpu,
  Server,
  Database,
  Network,
  Lock,
  Monitor,
  BarChart3,
  Globe,
  Code,
  Container,
  Play,
  Layers,
  Terminal,
  Check
} from 'lucide-react';

export const CloudPage = () => {
  const heroFeatures = [
    { icon: Cpu, label: 'High Performance' },
    { icon: Database, label: 'S3-Compatible Object Storage' },
    { icon: Network, label: 'Private Networking' },
    { icon: Monitor, label: 'Virtual Machines' },
    { icon: Container, label: 'Secure Containers' },
  ];

  const advantages = [
    {
      icon: DollarSign,
      title: 'Predictable Pricing',
      description: 'Fixed monthly costs. No surprise bills for bandwidth or API requests. 1TB storage included.'
    },
    {
      icon: Unlock,
      title: 'No Vendor Lock-In',
      description: 'Standard Linux VMs and containers. Move your workloads in and freely without proprietary hurdles.'
    },
    {
      icon: Shield,
      title: 'Sovereign & Secure',
      description: 'Owned and operated independently. Your data stays under your control, free from Big Tech oversight.'
    },
    {
      icon: Cpu,
      title: 'Bare Metal Power',
      description: 'Dedicated resources that don\'t share performance. Get the raw speed you pay for.'
    }
  ];

  const infrastructure = [
    {
      icon: Container,
      title: 'High Performance Containers',
      description: 'Run your applications with the speed of bare metal hardware, free from virtualization slowdowns.'
    },
    {
      icon: Database,
      title: 'S3-Compatible Storage',
      description: 'Store and retrieve any amount of data with standard S3 APIs. Seamlessly integrates with existing tools.'
    },
    {
      icon: Network,
      title: 'Dedicated Networking',
      description: 'Enjoy networking built with dedicated bandwidth to support performance even during traffic spikes.'
    },
    {
      icon: Lock,
      title: 'Hardened Security',
      description: 'Advanced isolation allows your applications to run in their own dedicated environments.'
    },
    {
      icon: Monitor,
      title: 'Virtual Machines',
      description: 'Need to run Linux or Windows? We support standard virtual machines alongside our high-speed containers.'
    },
    {
      icon: BarChart3,
      title: 'Deep Insights',
      description: 'Get real-time visibility into your application\'s performance to spot and fix issues instantly.'
    }
  ];

  const workloads = [
    {
      icon: Globe,
      title: 'Web Apps',
      items: ['Next.js', 'Rails', 'Laravel'],
      description: 'Run behind a load balancer, add TLS, scale up the package when needed.'
    },
    {
      icon: Terminal,
      title: 'APIs & Microservices',
      items: ['REST', 'GraphQL', 'gRPC'],
      description: 'Private networks for service-to-service traffic, snapshots/backups for safety.'
    },
    {
      icon: Container,
      title: 'Containers',
      items: ['Docker', 'Compose'],
      description: 'Run web + API + worker + Redis together in your own isolated stack.'
    },
    {
      icon: Play,
      title: 'Media Workloads',
      items: ['Video', 'Audio', 'Images'],
      description: 'Higher tiers are transcoding-capable. Pair with Parler CDN for delivery.'
    },
    {
      icon: Database,
      title: 'Databases',
      items: ['Postgres', 'Redis', 'MySQL'],
      description: 'Self-manage in containers or use our fully managed add-ons.'
    },
    {
      icon: Layers,
      title: 'Internal Tools',
      items: ['Dashboards', 'CI Runners'],
      description: 'Perfect for admin dashboards, cron jobs, staging envs, and runners.'
    }
  ];

  const addons = [
    { name: 'Extra Object Storage', price: '$0.02', unit: '/GB' },
    { name: 'Block Storage', price: '$0.10', unit: '/GB' },
    { name: 'Snapshot Storage', price: '$0.05', unit: '/GB' },
    { name: 'Load Balancer', price: '$12', unit: '/mo' },
    { name: 'Additional IPv4', price: '$4', unit: '/mo' },
    { name: 'Managed Postgres', price: 'From $15', unit: '/mo' },
    { name: 'Managed Redis', price: 'From $10', unit: '/mo' },
  ];

  const packageIncludes = [
    'Compute (vCPU + RAM)',
    'Local SSD/NVMe Storage',
    'Monthly Bandwidth Allowance',
    'Object Storage Allocation',
    'Private Networking'
  ];

  return (
    <div className="bg-[#0a0f1a] min-h-screen">
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 px-4 md:px-8 lg:px-12 text-center">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#2a5ff6]/10 border border-[#2a5ff6]/30 px-4 py-2 mb-8 text-sm text-[#2a5ff6] rounded-full">
            <Cloud size={16} />
            Parler Cloud
            <span className="bg-[#2a5ff6] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ml-1">
              Coming Soon
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4">
            Enterprise power.
            <br />
            <span className="text-[#2a5ff6]">Simplified for everyone.</span>
          </h1>

          <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto mb-10">
            Run securely on the independent cloud built for performance and predictability. 
            Escape the "Big Tech" tax with fixed pricing and zero hidden fees.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link 
              to="/packages?tab=cloud" 
              className="inline-flex items-center justify-center gap-2 bg-[#2a5ff6] text-white px-8 py-3.5 font-semibold hover:bg-[#4d7ff8] transition-all rounded-xl hover:-translate-y-1"
            >
              View Cloud Pricing
            </Link>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/20 text-white px-8 py-3.5 font-semibold hover:bg-white/5 transition-all rounded-xl hover:-translate-y-1"
            >
              Talk to Sales
            </Link>
          </div>

          {/* Hero Features Bar */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 text-white/50 text-sm">
            {heroFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="flex items-center gap-2">
                  <Icon size={16} className="text-[#2a5ff6]" />
                  <span className="font-mono">{feature.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Independent Cloud Advantage */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-[#0d1526]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
              The Independent Cloud Advantage
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              The capabilities of leading independent clouds, enhanced by our own performance innovations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {advantages.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-[#0a0f1a] border border-white/10 p-6 text-center hover:border-[#2a5ff6]/30 transition-all rounded-2xl hover:-translate-y-1">
                  <Icon size={32} className="text-[#2a5ff6] mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2 text-[#2a5ff6]">{item.title}</h3>
                  <p className="text-sm text-white/50">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Infrastructure Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-12">
            Infrastructure without the overhead
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {infrastructure.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-[#0d1526] border border-white/10 p-6 hover:border-[#2a5ff6]/30 transition-all rounded-2xl hover:-translate-y-1">
                  <Icon size={24} className="text-[#2a5ff6] mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-white/50">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You Can Run Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-[#0d1526]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-12">
            What you can run on Parler Cloud
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {workloads.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-[#0a0f1a] border border-white/10 p-6 hover:border-[#2a5ff6]/30 transition-all rounded-2xl hover:-translate-y-1">
                  <Icon size={24} className="text-white/30 mb-4" />
                  <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-[#2a5ff6] mb-2">{item.items.join(' • ')}</p>
                  <p className="text-sm text-white/50">{item.description}</p>
                </div>
              );
            })}
          </div>

          {/* Starter Recipes */}
          <div className="text-center">
            <p className="text-white/40 text-sm uppercase tracking-wider mb-4">Common Starter Recipes</p>
            <div className="flex flex-wrap justify-center gap-3">
              <span className="bg-[#2a5ff6]/20 text-[#2a5ff6] px-4 py-2 text-sm font-mono rounded-xl">
                <span className="bg-[#2a5ff6] text-white px-2 py-0.5 mr-2 text-xs rounded">WEB</span>
                Launch a website + Postgres
              </span>
              <span className="bg-[#F97316]/20 text-[#F97316] px-4 py-2 text-sm font-mono rounded-xl">
                <span className="bg-[#F97316] text-black px-2 py-0.5 mr-2 text-xs rounded">API</span>
                Run an API + Redis + Worker
              </span>
              <span className="bg-[#EF4444]/20 text-[#EF4444] px-4 py-2 text-sm font-mono rounded-xl">
                <span className="bg-[#EF4444] text-white px-2 py-0.5 mr-2 text-xs rounded">VID</span>
                Ingest → Transcode → CDN
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Pricing is package-based
              </h2>
              <p className="text-[#2a5ff6] text-lg mb-6">(not surprise-based)</p>
              
              <p className="text-white/60 mb-6">
                You pick a monthly package based on vCPU + RAM + SSD + Bandwidth. You can 
                upgrade or downgrade anytime. No complex calculators required.
              </p>

              <p className="text-white font-semibold mb-3">What's included in every package:</p>
              <div className="space-y-2">
                {packageIncludes.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-white/70">
                    <Check size={16} className="text-[#2a5ff6]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#0d1526] border border-white/10 p-6 rounded-2xl">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span className="text-[#2a5ff6]">+</span> Add-ons & Extras
              </h3>
              <div className="space-y-3">
                {addons.map((addon, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-white/70">{addon.name}</span>
                    <span className="text-[#2a5ff6] font-mono">
                      {addon.price}<span className="text-white/40 text-sm">{addon.unit}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-[#0d1526]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
            Deliver it with Parler CDN
          </h2>
          <p className="text-base md:text-lg text-white/60 mb-8">
            Your infrastructure is fast. Make delivery seamless. Pair Cloud with our global CDN.
          </p>
          <Link 
            to="/cdn" 
            className="inline-flex items-center gap-2 bg-transparent border border-[#2a5ff6] text-[#2a5ff6] px-8 py-3.5 font-semibold hover:bg-[#2a5ff6]/10 transition-all rounded-xl hover:-translate-y-1"
          >
            Explore Parler CDN
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Pricing Disclaimer */}
      <section className="px-4 md:px-8 lg:px-12 pt-16 md:pt-20 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs text-white/40 text-center leading-relaxed">
            Pricing excludes applicable taxes, regulatory charges, and third-party pass-through costs. References to "Big Tech tax" describe common variable pricing models in the market and are not a claim about any specific provider's pricing.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CloudPage;
